import React, { useState } from 'react'
import { formatEnvURL } from '../util/env'

const useApplicationAuth = () => {
    const [response, setResponse] = useState({
        status: undefined,
        message: undefined,
    })
    const [emailVerificationID, setEmailVerificationID] = useState()

    const initiateEmailVerificationCode = async (emailAddress) => {
        // creates an email auth code given an email address
        const res = await fetch(formatEnvURL('auth/authcode'), {
            method: 'PUT',
            body: JSON.stringify({
                email: emailAddress,
            })
        })
        
        if (res.status === 200) {
            return res.json()
        }

        try {
            const { message } = await res.json()
            setResponse({
                message: message, status: 'error'
            })           
        } catch {
            setResponse({
                message: "failed to process request", status: 'error'
            })           
        } 
        return undefined       
    }

    const onSignInFormSubmit = async (e) => {
        e.preventDefault()
        const formData = new FormData(e.target)
        const formValues = Object.fromEntries(formData.entries())

        const res = await fetch(formatEnvURL('auth/user-token'), {
            method: "POST",
            body: JSON.stringify(formValues)
        })

        if (res.status === 200) {
            window.location.href = "/"
            return
        }

        try {
            const jsonRes = await res.json()
            switch (jsonRes.code) {
                case "verify_email": {                    
                    const { verificationId, message } = await initiateEmailVerificationCode(formValues.email)
                    setEmailVerificationID(verificationId)
                    setResponse({ message: message, status: 'warning' })
                    break
                }
                default: {
                    setResponse({ message: jsonRes?.message, status: 'error' })
                    break
                }
            }
        } catch {
            setResponse({ message: "An error occurred when signing in, please try again later.", status: 'error' })
        }
    }

    const onSubmitCreateUser = async (e) => {
        e.preventDefault()
        const formData = new FormData(e.target)
        const formValues = Object.fromEntries(formData.entries())

        if (formValues.password !== formValues.confirmPassword) {    
            setResponse({
                status: 'error',
                message: 'Passwords do not match'
            })
            return
        }

        if (formValues.name === '' || formValues.email === '' || formValues.password === '') {
            setResponse({
                status: 'error',
                message: 'All fields must be filled'
            })
            return
        }

        const res = await fetch(formatEnvURL('accounts/create'), {
            method: 'POST',
            body: JSON.stringify(formValues),
        })

        if (res.status === 200) {
            setResponse({
                status: 'success',
                message: 'Account created, please login to continue'
            })            
            return true
        }

        // error happened
        try {
            const msg = await res.json()
            if (msg.message !== undefined) {
                setResponse({
                    status: 'error',
                    message: msg.message
                })
            } else {
                throw Error('')
            }
        } catch {
            setResponse({
                status: 'error',
                message: 'Error, account could not be created'
            })    
        }
    }

    const onSubmitEmailVerificationCode = async (e) => {
        e.preventDefault()
        const formData = new FormData(e.target)
        const formValues = Object.fromEntries(formData.entries())

        const res = await fetch(formatEnvURL('auth/verify-authcode'), {
            method: 'POST',
            body: JSON.stringify({
                id: emailVerificationID,
                code: formValues.code,
            })
        })
        
        if (res.status !== 200) {
            setResponse({status: 'error', message: 'Incorrect code, please try again'})
            return false
        } 

        return true
    }

    // onResetPassword requires that the verificationId already be set
    const onResetPassword = async (e, verificationId) => {
        e.preventDefault()
        const formData = new FormData(e.target)
        const formValues = Object.fromEntries(formData.entries())

        if (formValues?.password !== formValues?.confirmPassword) {
            setResponse({
                status: 'error',
                message: 'Passwords do not match'
            })
            return false
        }

        const res = await fetch(formatEnvURL('auth/reset'), {
            method: 'POST',
            body: JSON.stringify({
                id: verificationId,
                password: formValues.password,
            })
        })
        

        if (res.status !== 200) {
            setResponse({status: 'error', message: 'Incorrect code, please try again'})
            return false
        }

        setResponse({
            status: 'success',
            message: 'Password Change Successful'
        })

        return true
    }

    const onIsCodeValid = async (id, code) => {
        const res = await fetch(formatEnvURL('auth/valid'), {
            method: "POST",
            body: JSON.stringify({
                id: id,
                code: code,
            })
        })

        if (res.status === 200) {
            setResponse({
                message: undefined,
                status: undefined
            })
            return true
        }

        try{
            const { message } = await res.json()

            setResponse({
                status: 'error',
                message: message
            })
        } catch {
            setResponse({
                status: 'error',
                message: "Incorrect Code. Try Again"
            })
        }

        return false
    }

    return {
        shouldVerifyEmail: !!emailVerificationID,
        response,
        onResetPassword,
        initiateEmailVerificationCode,
        onSubmitEmailVerificationCode,
        onSubmitCreateUser,
        onSignInFormSubmit,
        onIsCodeValid,
        setResponse,
        resetMessage: () => setResponse({
            message: undefined,
            status: undefined,
        }),
    }
}

export default useApplicationAuth